import React, { useState, useEffect } from 'react';
import { Tabs, Card, Descriptions, List, Tag, Switch, Space, Typography, Table, Alert, Modal, Input, Button, message, Form, Checkbox, Tooltip } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { useFirestore } from '../../contexts/FirestoreContext';
import { ROLES, formatRoleDisplay, getRoleColor } from '../../models/role';
import { doc, getDoc, collection, getDocs, deleteDoc, updateDoc, query, where, addDoc, orderBy, limit } from 'firebase/firestore';
import { DeleteOutlined, ExclamationCircleOutlined, EditOutlined, StopOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { DIGITAL_PRODUCTS } from '../../models/digitalProducts';
import setupOrganisationProducts from '../../utils/setupOrganisationProducts.js';
import { fixUserData } from '../../utils/fixUserData';


const { Title, Text } = Typography;
const { confirm } = Modal;
const { TabPane } = Tabs;

const HARDCODED_TEMPLATES = [
  { id: 'crisis-plan', name: 'Crisis Plan' },
  { id: 'business-continuity-plan', name: 'Business Continuity Plan' },
  { id: 'crisis-communication-plan', name: 'Crisis Communication Plan' },
  { id: 'vendor-risk-management-plan', name: 'Vendor Risk Management Plan' },
  { id: 'post-incident-review-template', name: 'Post Incident Review template' },
  { id: 'high-risk-travel-plan', name: 'High Risk Travel Plan' },
  { id: 'medical-emergency-incident-response-guide', name: 'Medical Emergency Incident Response Guide' },
  { id: 'cyber-incident-incident-response-guide', name: 'Cyber Incident Incident Response Guide' },
  { id: 'critical-incident-incident-response-guide', name: 'Critical Incident Incident Response Guide' },
  { id: 'suspicious-item-bomb-threat-incident-response-guide', name: 'Suspicious Item/Bomb Threat Incident Response Guide' },
  { id: 'active-threat-incident-response-guide', name: 'Active Threat Incident Response Guide' },
  { id: 'fire-incident-response-guide', name: 'Fire Incident Response Guide' },
  { id: 'flood-incident-response-guide', name: 'Flood Incident Response Guide' },
  { id: 'earthquake-incident-response-guide', name: 'Earthquake Incident Response Guide' },
  { id: 'meeting-event-incident-incident-response-guide', name: 'Meeting/Event Incident Incident Response Guide' },
  { id: 'cyclone-incident-response-guide', name: 'Cyclone Incident Response Guide' },
  { id: 'kidnap-for-ransom-and-extortion-kre-incident-response-guide', name: 'Kidnap for Ransom and Extortion (KRE) Incident Response Guide' },
  { id: 'adverse-employee-separation-checklist', name: 'Adverse Employee Separation Checklist' },
  { id: 'evacuation-preparedness-checklist', name: 'Evacuation Preparedness Checklist' },
  { id: 'pandemic-readiness-checklist', name: 'Pandemic Readiness Checklist' },
  { id: 'resilience-readiness-framework', name: 'Resilience Readiness Framework' },
];

const ClientOrgManagement = ({ orgId, isEditMode = false }) => {
  console.log('=== CLIENT ORG MANAGEMENT COMPONENT ===');
  console.log('orgId:', orgId);
  console.log('isEditMode:', isEditMode);
  
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [orgData, setOrgData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [isDeactivateModalVisible, setIsDeactivateModalVisible] = useState(false);
  const [orgToDeactivate, setOrgToDeactivate] = useState(null);
  const [deactivateConfirmText, setDeactivateConfirmText] = useState('');
  const [organisations, setOrganisations] = useState([]);
  const [activeTab, setActiveTab] = useState('1');
  const [isEditing, setIsEditing] = useState(isEditMode);
  const [form] = Form.useForm();
  const [formData, setFormData] = useState(null);
  const navigate = useNavigate();
  const [detailedViewMode, setDetailedViewMode] = useState('view');
  const [selectedOrgForDetail, setSelectedOrgForDetail] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Check if user has permission to view this data
  const hasPermission = () => {
    if (currentUser?.isAdmin || currentUser?.role === 'developer') return true;
    if (!currentUser?.orgId || currentUser?.orgId !== orgId) return false;
    return [ROLES.CMT_LEADER, ROLES.CMT_LEADER_ALT, ROLES.CMT_ADMIN].includes(currentUser?.role);
  };

  const handleViewOrgConfig = (orgId) => {
    console.log('Viewing config for org:', orgId);
    setSelectedOrg({ id: orgId, isEditing: false });
  };

  const fetchOrgUsers = async (organizationId) => {
    if (!organizationId) {
      console.log('No organizationId provided to fetchOrgUsers');
      return;
    }
    
    try {
      setUsersLoading(true);
      
      // Query users from c3_users collection for this organization
      const usersRef = collection(db, 'c3_users');
      const usersQuery = query(
        usersRef,
        where('orgId', '==', organizationId)
      );
      
      const usersSnapshot = await getDocs(usersQuery);
      console.log('Found', usersSnapshot.docs.length, 'users for org:', organizationId);
      
      const usersList = usersSnapshot.docs.map(doc => {
        const userData = doc.data();
        console.log('Raw user data:', userData);
        return {
          id: doc.id,
          email: userData.email,
          fullName: userData.fullName,
          firstName: userData.firstName,
          lastName: userData.lastName,
          displayName: userData.displayName,
          cmtRole: userData.cmtRole || userData.role,
          lastLogin: userData.lastLogin,
          lastLoginAt: userData.lastLoginAt,
          orgId: userData.orgId
        };
      });
      
      console.log('Processed users list:', usersList);
      setOrgUsers(usersList);
    } catch (error) {
      console.error('Error fetching organization users:', error);
      setOrgUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchOrgData = async () => {
    if (!hasPermission()) {
      setError('You do not have permission to view this data');
      setLoading(false);
      return;
    }

    try {
      // Fetch all organisations
      const orgsRef = collection(db, 'c3_organisations');
      const orgsSnapshot = await getDocs(orgsRef);
      console.log('Raw organisations from Firestore:', orgsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
      
      const orgsList = orgsSnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Processing org data:', { id: doc.id, ...data });
        return {
          id: doc.id,
          ...data,
          clientStatus: data.clientStatus || 'active',
          createdAt: data.createdAt || new Date(),
          name: data.name || 'Unnamed Organisation',
          digitalProducts: data.digitalProducts || []
        };
      });
      console.log('Processed organisations list:', orgsList);
      setOrganisations(orgsList);

      // Fetch main org data if orgId is provided
      if (orgId) {
        const orgRef = doc(db, 'c3_organisations', orgId);
        const orgDoc = await getDoc(orgRef);
        
        if (!orgDoc.exists()) {
          throw new Error('Organisation not found');
        }

        // Get the organization data
        const orgData = orgDoc.data();

        // Fetch product mappings
        const orgProductsRef = collection(db, 'c3_organisation_products');
        const orgProductsQuery = query(
          orgProductsRef,
          where('organisationId', '==', orgId)
        );
        const orgProductsSnapshot = await getDocs(orgProductsQuery);
        const productIds = orgProductsSnapshot.docs.map(doc => doc.data().productId);

        // Fetch onboarding data
        const onboardingRef = collection(db, `c3_organisations/${orgId}/onboarding`);
        const onboardingSnapshot = await getDocs(onboardingRef);

        const onboardingData = {};
        onboardingSnapshot.forEach(doc => {
          onboardingData[doc.id] = doc.data();
        });

        setOrgData({
          ...orgData,
          id: orgDoc.id,
          onboarding: onboardingData,
          digitalProducts: productIds
        });
      }
    } catch (err) {
      console.error('Error fetching organisation data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (db) {
      fetchOrgData();
    }
  }, [db, orgId]);

  // Add debug logging for state changes
  useEffect(() => {
    console.log('orgData changed:', orgData);
  }, [orgData]);

  useEffect(() => {
    console.log('isEditing changed:', isEditing);
  }, [isEditing]);

  // Initialize form when orgData changes
  useEffect(() => {
    if (orgData) {
      console.log('Initializing form with orgData:', orgData);
      console.log('About to fetch users for org ID:', orgData.id);
      form.setFieldsValue(orgData);
      // Fetch users for this organization
      fetchOrgUsers(orgData.id);
    }
  }, [orgData, form]);

  const handleDeleteClick = (org) => {
    setOrgToDelete(org);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmText !== 'YES') {
      message.error('Please type YES to confirm deletion');
      return;
    }

    try {
      setLoading(true);
      // Delete the organization document
      await deleteDoc(doc(db, 'c3_organisations', orgToDelete.id));
      message.success('Organization deleted successfully');
      setIsDeleteModalVisible(false);
      setDeleteConfirmText('');
      setOrgToDelete(null);
      // Refresh the data
      fetchOrgData();
    } catch (error) {
      console.error('Error deleting organization:', error);
      message.error('Failed to delete organization');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setDeleteConfirmText('');
    setOrgToDelete(null);
  };

  const handleDeactivateClick = (org) => {
    setOrgToDeactivate(org);
    setIsDeactivateModalVisible(true);
  };

  const handleDeactivateConfirm = async () => {
    if (deactivateConfirmText !== 'YES') {
      message.error('Please type YES to confirm deactivation');
      return;
    }

    try {
      setLoading(true);
      // Update the organization's status to inactive
      await updateDoc(doc(db, 'c3_organisations', orgToDeactivate.id), {
        clientStatus: 'inactive'
      });
      message.success('Organization deactivated successfully');
      setIsDeactivateModalVisible(false);
      setDeactivateConfirmText('');
      setOrgToDeactivate(null);
      // Refresh the data
      fetchOrgData();
    } catch (error) {
      console.error('Error deactivating organization:', error);
      message.error('Failed to deactivate organization');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateCancel = () => {
    setIsDeactivateModalVisible(false);
    setDeactivateConfirmText('');
    setOrgToDeactivate(null);
  };

  const handleEdit = async (org) => {
    try {
      console.log('Editing org:', org);
      // Get current product mappings
      const orgProductsRef = collection(db, 'c3_organisation_products');
      const orgProductsQuery = query(
        orgProductsRef,
        where('organisationId', '==', org.id)
      );
      console.log('Querying org products with orgId:', org.id);
      
      const orgProductsSnapshot = await getDocs(orgProductsQuery);
      console.log('Raw org products docs:', orgProductsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
      
      const currentProductIds = orgProductsSnapshot.docs.map(doc => doc.data().productId);
      console.log('Current product IDs:', currentProductIds);

      setSelectedOrgForDetail({
        ...org,
        digitalProducts: currentProductIds
      });
      setDetailedViewMode('edit');
    } catch (error) {
      console.error('Error loading org details:', error);
      message.error('Failed to load organization details');
    }
  };

  const handleSaveOrgDetails = async () => {
    try {
      console.log('=== DETAILED VIEW SAVE CLICKED ===');
      setLoading(true);
      console.log('Saving org details for:', selectedOrgForDetail);
      const orgRef = doc(db, 'c3_organisations', selectedOrgForDetail.id);
      
      // Update org details
      await updateDoc(orgRef, {
        name: selectedOrgForDetail.name,
        subscriptionPlan: selectedOrgForDetail.subscriptionPlan,
        industry: selectedOrgForDetail.industry,
        description: selectedOrgForDetail.description,
        abn: selectedOrgForDetail.abn,
        primaryAddress: selectedOrgForDetail.primaryAddress,
        timezone: selectedOrgForDetail.timezone,
        updatedAt: new Date(),
        updatedBy: currentUser.uid
      });

      // Update digital product access
      if (selectedOrgForDetail.digitalProducts) {
        console.log('Updating digital products access:', selectedOrgForDetail.digitalProducts);
        // The digitalProducts array is already an array of IDs from the Checkbox.Group
        await setupOrganisationProducts(db, selectedOrgForDetail.id, selectedOrgForDetail.subscriptionPlan, selectedOrgForDetail.digitalProducts, currentUser);
      }

      message.success('Organization details updated successfully');
      setDetailedViewMode('view');
      fetchOrgData(); // Refresh the list
    } catch (error) {
      console.error('Error updating org details:', error);
      message.error('Failed to update organization details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMainForm = async () => {
    try {
      console.log('=== MAIN FORM SAVE CLICKED ===');
      setLoading(true);
      console.log('Saving main form data:', orgData);
      console.log('Digital products to save:', orgData.digitalProducts);
      
      const orgRef = doc(db, 'c3_organisations', orgData.id);
      
      // Update org details
      await updateDoc(orgRef, {
        name: orgData.name,
        subscriptionPlan: orgData.subscriptionPlan,
        industry: orgData.industry,
        description: orgData.description,
        abn: orgData.abn,
        primaryAddress: orgData.primaryAddress,
        timezone: orgData.timezone,
        digitalProducts: orgData.digitalProducts || [],
        updatedAt: new Date(),
        updatedBy: currentUser.uid
      });
      console.log('Organization document updated successfully');

      // Update product mappings
      console.log('Updating product mappings...');
      const orgProductsRef = collection(db, 'c3_organisation_products');
      
      // First, delete existing mappings
      console.log('Deleting existing mappings...');
      const existingMappingsQuery = query(
        orgProductsRef,
        where('organisationId', '==', orgData.id)
      );
      const existingMappings = await getDocs(existingMappingsQuery);
      const deletePromises = existingMappings.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      console.log('Existing mappings deleted');

      // Then create new mappings
      console.log('Creating new mappings...');
      const newMappings = (orgData.digitalProducts || []).map(productId => ({
        organisationId: orgData.id,
        productId: productId,
        createdAt: new Date()
      }));
      console.log('New mappings to create:', newMappings);

      const createPromises = newMappings.map(mapping => 
        addDoc(orgProductsRef, mapping)
      );
      await Promise.all(createPromises);
      console.log('New mappings created successfully');

      message.success('Organization details updated successfully');
      setIsEditing(false);
      fetchOrgData(); // Refresh the list
    } catch (error) {
      console.error('Error updating org details:', error);
      message.error('Failed to update organization details');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      console.log('=== SAVE CHANGES CLICKED ===');
      console.log('Selected product IDs:', orgData.digitalProducts);
      console.log('Organization ID:', orgId);
      
      if (!orgId) {
        console.error('No organization ID found');
        return;
      }

      // Update organization document
      console.log('Updating organization document...');
      const orgRef = doc(db, 'c3_organisations', orgId);
      await updateDoc(orgRef, {
        digitalProducts: orgData.digitalProducts
      });
      console.log('Organization document updated successfully');

      // Update product mappings
      console.log('Updating product mappings...');
      const orgProductsRef = collection(db, 'c3_organisation_products');
      
      // First, delete existing mappings
      console.log('Deleting existing mappings...');
      const existingMappingsQuery = query(
        orgProductsRef,
        where('organisationId', '==', orgId)
      );
      const existingMappings = await getDocs(existingMappingsQuery);
      const deletePromises = existingMappings.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      console.log('Existing mappings deleted');

      // Then create new mappings
      console.log('Creating new mappings...');
      const newMappings = orgData.digitalProducts.map(productId => ({
        organisationId: orgId,
        productId: productId,
        createdAt: new Date()
      }));
      console.log('New mappings to create:', newMappings);

      const createPromises = newMappings.map(mapping => 
        addDoc(orgProductsRef, mapping)
      );
      await Promise.all(createPromises);
      console.log('New mappings created successfully');

      message.success('Changes saved successfully');
      fetchOrgData(); // Refresh the data
    } catch (error) {
      console.error('Error saving changes:', error);
      setError(error.message);
    }
  };

  const handleFixUserData = async () => {
    try {
      setLoading(true);
      const result = await fixUserData();
      message.success(`User data fixed! Processed ${result.processed} users, updated ${result.updated} users.`);
      // Refresh the users for this organization
      if (orgData?.id) {
        fetchOrgUsers(orgData.id);
      }
    } catch (error) {
      console.error('Error fixing user data:', error);
      message.error('Failed to fix user data');
    } finally {
      setLoading(false);
    }
  };

  const renderEditableField = (field, label, type = 'text') => {
    const value = orgData?.[field];
    console.log(`Rendering field ${field} with value:`, value);

    if (isEditing) {
      return (
        <Form.Item
          name={field}
          label={label}
          rules={[{ required: true, message: `Please input ${label.toLowerCase()}!` }]}
        >
          {type === 'textarea' ? (
            <Input.TextArea 
              rows={4} 
              value={value}
              onChange={(e) => {
                const newData = { ...orgData, [field]: e.target.value };
                setOrgData(newData);
              }}
            />
          ) : (
            <Input 
              value={value}
              onChange={(e) => {
                const newData = { ...orgData, [field]: e.target.value };
                setOrgData(newData);
              }}
            />
          )}
        </Form.Item>
      );
    }
    return (
      <Descriptions.Item label={<strong>{label}</strong>}>
        {value || 'Not set'}
      </Descriptions.Item>
    );
  };

  const renderEditableSwitch = (field, label) => {
    const value = orgData?.[field];
    console.log(`Rendering switch ${field} with value:`, value);

    if (isEditing) {
      return (
        <Form.Item
          name={field}
          label={label}
          valuePropName="checked"
        >
          <Switch 
            checked={value}
            onChange={(checked) => {
              const newData = { ...orgData, [field]: checked };
              setOrgData(newData);
            }}
          />
        </Form.Item>
      );
    }
    return (
      <Descriptions.Item label={label}>
        <Switch checked={value} disabled />
      </Descriptions.Item>
    );
  };

  // Debug log before render
  console.log('Rendering with orgData:', orgData);
  console.log('Rendering with isEditing:', isEditing);
  console.log('Rendering with form:', form);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!orgData) return <div>No data available</div>;

  const items = [
    {
      key: '1',
      label: <strong>Basic Information</strong>,
      children: (
        <Card>
          <Descriptions title="Organisation Details" bordered>
            {renderEditableField('name', 'Organisation Name')}
            {renderEditableField('subscriptionPlan', 'Subscription Plan')}
            {renderEditableField('industry', 'Industry Sector')}
            {renderEditableField('timezone', 'Timezone')}
            {renderEditableField('abn', 'ABN/Legal Entity')}
            {renderEditableField('description', 'Description')}
            {renderEditableField('primaryAddress', 'Primary Address')}
          </Descriptions>
        </Card>
      )
    },
    {
      key: '2',
      label: <strong>Pricing and Revenue</strong>,
      children: (
        <Card>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Title level={5}>Plan and Subscription Details</Title>
              <Descriptions bordered>
                {renderEditableField('subscriptionPlan', 'Current Plan')}
                {renderEditableField('monthlyRate', 'Monthly Rate')}
                {renderEditableField('contractStartDate', 'Contract Start Date')}
                {renderEditableField('contractEndDate', 'Contract End Date')}
                {renderEditableField('billingFrequency', 'Billing Frequency')}
                {renderEditableField('discountApplied', 'Discount Applied')}
              </Descriptions>
            </div>

            <div>
              <Title level={5}>Total Annual Contract Value</Title>
              <Descriptions bordered>
                {renderEditableField('basePlanValue', 'Base Plan')}
                {renderEditableField('addOnsValue', 'Add-Ons')}
                {renderEditableField('ytdUsageCharges', 'Usage Charges (YTD)')}
                {renderEditableField('totalRevenue', 'Total Revenue to Date')}
              </Descriptions>
            </div>

            <div>
              <Title level={5}>Upcoming Renewal and Billing Status</Title>
              <Descriptions bordered>
                {renderEditableField('nextInvoiceDate', 'Next Invoice Date')}
                {renderEditableSwitch('autoRenewalEnabled', 'Auto-Renewal Status')}
                {renderEditableField('accountRep', 'Account Rep')}
                {renderEditableField('paymentMethod', 'Payment Method')}
                {renderEditableField('lastPaymentStatus', 'Last Payment Status')}
              </Descriptions>
            </div>
          </Space>
        </Card>
      )
    },
    {
      key: '3',
      label: <strong>Notification Preferences</strong>,
      children: (
        <Card>
          <Descriptions title="Notification Settings" bordered>
            {renderEditableSwitch('alertTaskUpdates', 'Email Notifications')}
            {renderEditableSwitch('alertIncidentEscalations', 'SMS Notifications')}
            {renderEditableSwitch('alertTaskUpdates', 'Phone Notifications')}
          </Descriptions>
        </Card>
      )
    },
    {
      key: '4',
      label: <strong>Templates and AI Features</strong>,
      children: (
        <Card>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Title level={5}>AI Features</Title>
              <Descriptions bordered>
                {renderEditableSwitch('aiSnapshotEnabled', 'AI Snapshot Generation')}
                {renderEditableSwitch('aiPirEnabled', 'AI PIR')}
                {renderEditableSwitch('aiCommsEnabled', 'AI Communications Assistant')}
              </Descriptions>
            </div>
          </Space>
        </Card>
      )
    },
    {
      key: '5',
      label: <strong>Organisation Users</strong>,
      children: (
        <Card>
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <Title level={5}>Organisation Users ({orgUsers.length})</Title>
              <Button 
                onClick={handleFixUserData}
                type="primary"
                size="small"
                loading={loading}
              >
                Fix User Data
              </Button>
            </div>
            {usersLoading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading users...</span>
                </div>
              </div>
            ) : orgUsers.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>CMT Role</th>
                      <th>Last Login Date/Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgUsers.map(user => (
                      <tr key={user.id}>
                        <td>
                          {(() => {
                            console.log('=== CLIENT ORG NAME DISPLAY LOGIC ===');
                            console.log('User for name display:', user);
                            console.log('fullName:', user.fullName, 'type:', typeof user.fullName);
                            console.log('firstName:', user.firstName, 'type:', typeof user.firstName);
                            console.log('lastName:', user.lastName, 'type:', typeof user.lastName);
                            console.log('displayName:', user.displayName, 'type:', typeof user.displayName);
                            
                            // Try fullName first (but reject if it looks like email username)
                            if (user.fullName && user.fullName.trim() && !user.fullName.includes('@') && user.fullName !== user.email?.split('@')[0]) {
                              console.log('Using fullName:', user.fullName.trim());
                              return user.fullName.trim();
                            }
                            
                            // Try firstName + lastName
                            if (user.firstName && user.lastName) {
                              const result = `${user.firstName.trim()} ${user.lastName.trim()}`;
                              console.log('Using firstName + lastName:', result);
                              return result;
                            }
                            
                            // Try just firstName
                            if (user.firstName && user.firstName.trim()) {
                              console.log('Using firstName only:', user.firstName.trim());
                              return user.firstName.trim();
                            }
                            
                            // Try displayName
                            if (user.displayName && user.displayName.trim()) {
                              console.log('Using displayName:', user.displayName.trim());
                              return user.displayName.trim();
                            }
                            
                            // Try email username
                            if (user.email) {
                              const result = user.email.split('@')[0];
                              console.log('Using email username:', result);
                              return result;
                            }
                            
                            // Last resort
                            console.log('Using N/A fallback');
                            return 'N/A';
                          })()}
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <span 
                            className="badge" 
                            style={{ 
                              backgroundColor: getRoleColor(user.role, user.cmtRole),
                              color: 'white',
                              fontWeight: '500'
                            }}
                          >
                            {formatRoleDisplay(user.role, user.cmtRole)}
                          </span>
                        </td>
                        <td>
                          {user.lastLogin && user.lastLogin.toDate 
                            ? user.lastLogin.toDate().toLocaleString()
                            : user.lastLoginAt && user.lastLoginAt.toDate
                            ? user.lastLoginAt.toDate().toLocaleString()
                            : 'Never'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-muted">No users found for this organization</div>
            )}
          </div>
        </Card>
      )
    },
    {
      key: '6',
      label: <strong>Crisis Plan</strong>,
      children: (
        <Card>
          <div className="d-flex flex-column align-items-start">
            <Title level={5}>Crisis Plan Management</Title>
            <p className="text-muted mb-4">View and manage this organization's Crisis Plan</p>
            <Button
              type="primary"
              size="large"
                                onClick={() => navigate(`/digital-products/crisis_plan/${orgData.id}`)}
            >
              View Crisis Plan
            </Button>
          </div>
        </Card>
      )
    }
  ];

  const columns = [
    {
      title: 'Organisation',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => record.name || record.companyName || 'Unnamed Organisation'
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <span className={`badge ${record.onboardingComplete ? 'bg-success' : 'bg-warning'}`}>
          {record.onboardingComplete ? 'Complete' : 'Incomplete'}
        </span>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            onClick={() => handleViewOrgConfig(record.id)}
            style={{ minWidth: '100px' }}
          >
            View Config
          </Button>
          <Button
            type="primary"
            onClick={() => handleEdit(record)}
            style={{ minWidth: '100px' }}
          >
            Edit
          </Button>
          <Button
            danger
            type="primary"
            icon={<StopOutlined />}
            onClick={() => handleDeactivateClick(record)}
            style={{ minWidth: '120px' }}
          >
            Deactivate
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div className="client-org-management">
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : orgData ? (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveMainForm}
          initialValues={orgData}
        >
          <Tabs 
            defaultActiveKey="1" 
            type="card"
            size="large"
          >
            <Tabs.TabPane 
              tab={
                <Tooltip title="View and edit basic organization information and contact details">
                  <strong>Basic Information</strong>
                </Tooltip>
              } 
              key="1"
            >
              <Card>
                <Descriptions title="Organisation Details" bordered>
                  {renderEditableField('name', 'Organisation Name')}
                  {renderEditableField('subscriptionPlan', 'Subscription Plan')}
                  {renderEditableField('industry', 'Industry Sector')}
                  {renderEditableField('timezone', 'Timezone')}
                  {renderEditableField('abn', 'ABN/Legal Entity')}
                  {renderEditableField('description', 'Description')}
                  {renderEditableField('primaryAddress', 'Primary Address')}
                </Descriptions>
              </Card>
            </Tabs.TabPane>

            <Tabs.TabPane 
              tab={
                <Tooltip title="Manage pricing plans, billing information, and subscription details">
                  <strong>Pricing and Revenue</strong>
                </Tooltip>
              } 
              key="2"
            >
              <Card>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <div>
                    <Title level={5}>Plan and Subscription Details</Title>
                    <Descriptions bordered>
                      {renderEditableField('subscriptionPlan', 'Current Plan')}
                      {renderEditableField('monthlyRate', 'Monthly Rate')}
                      {renderEditableField('contractStartDate', 'Contract Start Date')}
                      {renderEditableField('contractEndDate', 'Contract End Date')}
                      {renderEditableField('billingFrequency', 'Billing Frequency')}
                      {renderEditableField('discountApplied', 'Discount Applied')}
                    </Descriptions>
                  </div>
                </Space>
              </Card>
            </Tabs.TabPane>

            <Tabs.TabPane 
              tab={
                <Tooltip title="Configure notification preferences and alert settings">
                  <strong>Notification Preferences</strong>
                </Tooltip>
              } 
              key="3"
            >
              <Card>
                <Descriptions title="Notification Settings" bordered>
                  {renderEditableSwitch('alertTaskUpdates', 'Email Notifications')}
                  {renderEditableSwitch('alertIncidentEscalations', 'SMS Notifications')}
                  {renderEditableSwitch('alertTaskUpdates', 'Phone Notifications')}
                </Descriptions>
              </Card>
            </Tabs.TabPane>

            <Tabs.TabPane 
              tab={
                <Tooltip title="Manage AI features, templates, and automation settings">
                  <strong>Templates and AI Features</strong>
                </Tooltip>
              } 
              key="4"
            >
              <Card>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  <div>
                    <Title level={5}>AI Features</Title>
                    <Descriptions bordered>
                      {renderEditableSwitch('aiSnapshotEnabled', 'AI Snapshot Generation')}
                      {renderEditableSwitch('aiPirEnabled', 'AI PIR')}
                      {renderEditableSwitch('aiCommsEnabled', 'AI Communications Assistant')}
                    </Descriptions>
                  </div>
                </Space>
              </Card>
            </Tabs.TabPane>

            <Tabs.TabPane 
              tab={
                <Tooltip title="Access and manage the organization's crisis management plan">
                  <strong>Crisis Plan</strong>
                </Tooltip>
              } 
              key="6"
            >
              <Card>
                <div className="d-flex flex-column align-items-start">
                  <Title level={5}>Crisis Plan Management</Title>
                  <p className="text-muted mb-4">View and manage this organization's Crisis Plan</p>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => navigate(`/digital-products/crisis_plan/${orgData.id}`)}
                  >
                    View Crisis Plan
                  </Button>
                </div>
              </Card>
            </Tabs.TabPane>

            <Tabs.TabPane 
              tab={
                <Tooltip title="Manage access to digital products and resources">
                  <strong>Digital Products</strong>
                </Tooltip>
              } 
              key="7"
            >
              <Card>
                <div className="d-flex flex-column align-items-start">
                  <Title level={5}>Digital Product Access</Title>
                  <p className="text-muted mb-4">Manage which digital products this organization has access to</p>
                  <div className="w-100">
                    <Form.Item name="digitalProducts">
                      <Checkbox.Group
                        onChange={value => {
                          console.log('=== PRODUCT SELECTION ===');
                          console.log('Selected products:', value);
                          form.setFieldsValue({ digitalProducts: value });
                          setIsEditing(true);
                        }}
                      >
                        <Space direction="vertical">
                          {Object.values(DIGITAL_PRODUCTS).map(product => (
                            <Checkbox key={product.id} value={product.id}>
                              {product.name}
                              <div className="text-muted" style={{ fontSize: '0.8em', marginLeft: '24px' }}>
                                {product.description}
                              </div>
                            </Checkbox>
                          ))}
                        </Space>
                      </Checkbox.Group>
                    </Form.Item>
                  </div>
                </div>
              </Card>
            </Tabs.TabPane>
          </Tabs>

          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="h4 mb-0">
              {isEditing ? 'Edit Organisation' : 'Organisation Details'}
            </h3>
            <Space>
              {isEditing ? (
                <>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => {
                      console.log('=== SAVE BUTTON CLICKED ===');
                      console.log('Form values:', form.getFieldsValue());
                      form.submit();
                    }}
                    loading={loading}
                  >
                    Save Changes
                  </Button>
                  <Button
                    onClick={() => {
                      setIsEditing(false);
                      form.resetFields();
                    }}
                    icon={<CloseOutlined />}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => setIsEditing(true)}
                >
                  Edit Details
                </Button>
              )}
            </Space>
          </div>
        </Form>
      ) : (
        <div className="alert alert-warning" role="alert">
          No organisation data found
        </div>
      )}

      {detailedViewMode === 'edit' && (
        <div className="mb-4">
          <h5>Digital Products Access</h5>
          <Checkbox.Group
            value={selectedOrgForDetail.digitalProducts}
            onChange={value => setSelectedOrgForDetail({
              ...selectedOrgForDetail,
              digitalProducts: value
            })}
          >
            <Space direction="vertical">
              {Object.values(DIGITAL_PRODUCTS).map(product => (
                <Checkbox key={product.id} value={product.id}>
                  {product.name}
                  <div className="text-muted" style={{ fontSize: '0.8em', marginLeft: '24px' }}>
                    {product.description}
                  </div>
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        </div>
      )}

      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            <span>Deactivate Organization</span>
          </Space>
        }
        open={isDeactivateModalVisible}
        onOk={handleDeactivateConfirm}
        onCancel={handleDeactivateCancel}
        okText="Deactivate"
        okButtonProps={{ 
          danger: true,
          disabled: deactivateConfirmText !== 'YES'
        }}
        cancelText="Cancel"
      >
        <div style={{ marginBottom: 16 }}>
          <p>Are you sure you want to deactivate <strong>{orgToDeactivate?.name}</strong>?</p>
          <p>This will revoke their access to the platform. You can reactivate them later if needed.</p>
        </div>
        <div>
          <p>Type <strong>YES</strong> to confirm:</p>
          <Input
            value={deactivateConfirmText}
            onChange={(e) => setDeactivateConfirmText(e.target.value)}
            placeholder="Type YES to confirm"
          />
        </div>
      </Modal>

      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            <span>Delete Organization</span>
          </Space>
        }
        open={isDeleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Delete"
        okButtonProps={{ 
          danger: true,
          disabled: deleteConfirmText !== 'YES'
        }}
        cancelText="Cancel"
      >
        <div style={{ marginBottom: 16 }}>
          <p>Are you sure you want to delete <strong>{orgToDelete?.name}</strong>?</p>
          <p>This action cannot be undone. All data associated with this organization will be permanently deleted.</p>
        </div>
        <div>
          <p>Type <strong>YES</strong> to confirm:</p>
          <Input
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="Type YES to confirm"
          />
        </div>
      </Modal>
    </div>
  );
};

export default ClientOrgManagement; 